import type {
  RecaptchaRequestConfig,
  RequestErrorInfo,
  RequestErrorData,
  SignInBody,
  User,
  SignIn,
  AuthToken,
  ParticipantScore,
  Participant,
  UserHighestScore,
  UserUpdate,
  Address,
  LeaderboardParams,
  LeaderboardResponse,
  CampaignGame,
  Campaign,
  CampaignUnit,
  Challenge,
  TicketCode,
  Prize,
  TicketCodeResponse,
  CampaignTranslations
} from './types';

import axios from 'axios';
import Store from '@/redux';

import {
  getAccessToken,
  getRefreshToken,
  getAccessExpiration,
  getRefreshExpiration
} from '@/redux/auth/selectors';

import {
  setCampaignData,
  setCampaignServerTime
} from '@/redux/campaign/actions';

import {
  setChallengeData,
  setChallengesData
} from '@/redux/challenges/actions';

import { reCAPTCHA } from '@/utils/API';
import type { AxiosError } from 'axios';
import { getAuthCode } from '@/services/app/mcd';
import { setAuthData } from '@/redux/auth/actions';
import { getUserState } from '@/redux/user/selectors';
import { setUserData, setUserPrize } from '@/redux/user/actions';
import type { Langs, UserState } from '@/redux/user/types';
import { getCampaignId, getCampaignGames } from '@/redux/campaign/selectors';

class APIService {
  private readonly games = [
    'doodle-jump-1',
    'jump-and-run',
    'tap-and-smash',
    'doodle-jump-4'
  ];
  private readonly anonymousUrls = ['/auth/token/refresh', '/auth/signin'];
  private readonly requests: Map<string, number> = new Map();

  private readonly axios = axios.create({
    headers: { common: { 'x-api-key': import.meta.env.VITE_API_KEY } },
    baseURL: `${import.meta.env.VITE_API_BASE_URL}/api`,
    withCredentials: true
  });

  public constructor() {
    this.createInterceptors();
  }

  private async updateExpiredToken(
    state = Store.getState(),
    now = Date.now()
  ): Promise<void> {
    // Force premature expiration if token will expire in 5 seconds:
    const accessExpiration = (getAccessExpiration(state) ?? now) - 5e3;
    const refreshExpiration = (getRefreshExpiration(state) ?? now) - 5e3;

    if (refreshExpiration <= now) {
      await this.login(getUserState(state));
    } else if (accessExpiration <= now) {
      await this.refreshToken(getRefreshToken(state));
    }
  }

  private createInterceptors(): void {
    this.axios.interceptors.request.use(async (config) => {
      this.updateRequests(config.url, true);

      if (config?.url && !this.anonymousUrls.includes(config.url)) {
        await this.updateExpiredToken();
        const accessToken = getAccessToken(Store.getState());

        if (accessToken && config?.headers) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(
      async (response) => {
        Store.dispatch(setCampaignServerTime(response.headers.date));
        this.updateRequests(response.config.url);
        return response;
      },

      async (error) => {
        this.updateRequests(error.config?.url);
        return Promise.reject(error);
      }
    );
  }

  private updateRequests(url = '', add = false): void {
    let pi = url.indexOf('?');
    pi = pi < 0 ? url.length : pi;
    const baseUrl = url.slice(0, pi);

    const current = this.requests.get(baseUrl) ?? 0;
    this.requests.set(baseUrl, Math.max(current + +add * 2 - 1, 0));
  }

  private startedRequests(...requests: string[]): boolean {
    for (let r = requests.length; r--; ) {
      if (this.requests.get(requests[r])) {
        return true;
      }
    }

    return false;
  }

  private async getReCaptchaConfig(): RecaptchaRequestConfig {
    if (!(window as Application).grecaptcha) {
      if (import.meta.env.VITE_RECAPTCHA_ENTERPRISE_KEY)
        throw new Error('ReCaptcha not initialized.');

      return;
    }

    try {
      const { enterprise } = (window as Application).grecaptcha as GreCaptcha;
      const token = await enterprise.execute(reCAPTCHA, { action: 'post' });
      return { headers: { 'x-recaptcha-token': token } };
    } catch {
      throw new Error('ReCaptcha not fetched.');
    }
  }

  public getRequestErrorInfo(axiosError: AxiosError): RequestErrorInfo {
    const { status = undefined, statusText = undefined } =
      axiosError.response ?? {};

    const {
      statusCode = undefined,
      message = undefined,
      error = undefined
    } = (axiosError.response?.data as RequestErrorData) ?? {};

    return {
      message: message || axiosError.message,
      code: statusCode || status,
      error: error || statusText
    };
  }

  /**
   * Auth Endpoints:
   */
  public revokeToken(): void {
    Store.dispatch(
      setAuthData({
        refreshExpiresAt: null,
        accessExpiresAt: null,
        refreshToken: null,
        accessToken: null
      })
    );
  }

  private getAccessExpiration(delay = 0): number {
    return Date.now() + delay * 1e3;
  }

  public async login(user: UserState): Promise<void> {
    const slug = this.getCampaignSlug(user.country);

    if (!this.startedRequests(`/campaigns/${slug}`, '/auth/signin')) {
      const casAuthToken =
        import.meta.env.VITE_CAS_CLIENT_ID &&
        (await getAuthCode(import.meta.env.VITE_CAS_CLIENT_ID));

      const campaign = await this.setCampaignData(user);

      const { challenges, ...me } = await this.signIn({
        firstName: user.firstname,
        campaignId: campaign.id,
        lastName: user.lastname,
        deviceId: user.deviceId,
        email: user.email,
        casAuthToken
      });

      challenges &&
        Store.dispatch(
          setChallengesData({ challenges: challenges as Challenge[] })
        );

      const language =
        user?.language ?? (import.meta.env.VITE_USER_LANGUAGE as Langs);

      this.setCampaignTranslations(campaign.id, language, '').then(
        (translations) =>
          Store.dispatch(setCampaignData({ ...campaign, translations }))
      );

      Store.dispatch(setUserData({ ...user, ...me }));
    }
  }

  private async signIn(data: SignInBody): Promise<User> {
    const config = await this.getReCaptchaConfig();

    const { token, me } = (
      await this.axios.post<SignIn>('/auth/signin', data, config)
    ).data;

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      token;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return me as User;
  }

  private async refreshToken(token: string | null): Promise<AuthToken | void> {
    const { data } = await this.axios.post<AuthToken>('/auth/token/refresh', {
      refreshToken: token
    });

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      data;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return data;
  }

  /**
   * User Endpoints:
   */
  public async submitScore(
    id: string,
    data: ParticipantScore
  ): Promise<Participant> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.patch<Participant>(
        `/participants/${id}/score`,
        data,
        config
      )
    ).data;
  }

  public async getUser(): Promise<User> {
    const { challenges, ...user } = (await this.axios.get<User>('/users/me'))
      .data;

    challenges &&
      Store.dispatch(
        setChallengesData({ challenges: challenges as Challenge[] })
      );

    Store.dispatch(setUserData(user as UserState));

    return user;
  }

  public async getHighestScore(index: number): Promise<number> {
    const { id } = this.getCampaignGame(index);

    return (
      await this.axios.get<UserHighestScore>(
        `/campaign-games/${id}/my-highest-score`
      )
    ).data.score;
  }

  public async updateUser(data: UserUpdate): Promise<User> {
    const config = await this.getReCaptchaConfig();

    const { challenges, ...user } = (
      await this.axios.patch<User>('/users/me', data, config)
    ).data;

    challenges &&
      Store.dispatch(
        setChallengesData({ challenges: challenges as Challenge[] })
      );

    Store.dispatch(setUserData(user as UserState));

    return user;
  }

  public async runLottery(id: string): Promise<Participant> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.patch<Participant>(
        `/participants/${id}/lottery`,
        undefined,
        config
      )
    ).data;
  }

  public async createLotteryParticipant(index: number): Promise<Participant> {
    const config = await this.getReCaptchaConfig();
    const { id } = this.getCampaignGame(index);

    return (
      await this.axios.post<Participant>(
        '/participants/with-lottery',
        { campaignGameId: id },
        config
      )
    ).data;
  }

  public async createParticipant(index: number): Promise<Participant> {
    if (!this.startedRequests('/participants')) {
      const config = await this.getReCaptchaConfig();
      const { id } = this.getCampaignGame(index);

      return (
        await this.axios.post<Participant>(
          '/participants',
          { campaignGameId: id },
          config
        )
      ).data;
    }

    throw new Error('Already creating a participant.');
  }

  public async updateAddress(id: string, data: Address): Promise<Participant> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.patch<Participant>(
        `/participants/${id}/address`,
        data,
        config
      )
    ).data;
  }

  /**
   * Campaign Endpoints:
   */
  public async getCampaignLeaderboard(
    index: number,
    params: LeaderboardParams
  ): Promise<LeaderboardResponse> {
    const { id } = this.getCampaignGame(index);

    return (
      await this.axios.get<LeaderboardResponse>(
        `/campaign-games/${id}/leaderboard`,
        { params }
      )
    ).data;
  }

  public getGameName(index: number): string {
    return this.games[index];
  }

  private async setCampaignData(user?: UserState): Promise<Campaign> {
    const slug = this.getCampaignSlug(user?.country);
    const campaign = await this.getCampaignBySlug(slug);

    Store.dispatch(setCampaignData({ ...campaign, translations: void 0 }));
    return campaign;
  }

  private async setCampaignTranslations(
    campaignId: string,
    language: Langs,
    separator = '_'
  ): Promise<Record<string, string>> {
    const translations: Record<string, string> = {};

    const campaignTranslations = await this.getCampaignTranslations(
      campaignId,
      language
    );

    Array.isArray(campaignTranslations) &&
      campaignTranslations.reduce((translations, { key, data }) => {
        for (const d in data) translations[`${key}${separator}${d}`] = data[d];

        return translations;
      }, translations);

    return translations;
  }

  private async getCampaignTranslations(
    campaignId: string,
    locale: string
  ): Promise<CampaignTranslations | void> {
    if (this.startedRequests('/translations')) return;

    return (
      await this.axios.get<CampaignTranslations>('/translations', {
        params: { campaignId, locale }
      })
    ).data;
  }

  private getCampaignSlug(country?: string): string {
    return `${import.meta.env.VITE_CAMPAIGN_BASE_SLUG}${(country && `-${country}`) || ''}`;
  }

  public getCampaignGame(index: number): CampaignGame {
    const state = Store.getState();
    const game = this.getGameName(index);

    return getCampaignGames(state).find(
      ({ slug }) => slug === game
    ) as CampaignGame;
  }

  private async getCampaignBySlug(slug: string): Promise<Campaign> {
    return (await this.axios.get<Campaign>(`/campaigns/${slug}`)).data;
  }

  public async getCampaignUnits(): Promise<CampaignUnit | undefined> {
    const campaignId = getCampaignId(Store.getState());

    return (
      await this.axios.get<CampaignUnit[]>(
        `campaign-units?campaignId=${campaignId}`
      )
    ).data[0];
  }

  /**
   * Challenges Endpoints:
   */
  public async getChallenges(): Promise<Challenge[]> {
    const challenges = (await this.axios.get<Challenge[]>('/challenges')).data;
    Store.dispatch(setChallengesData({ challenges }));
    return challenges;
  }

  public async getChallenge(id: string): Promise<Challenge> {
    const challenge = (await this.axios.get<Challenge>(`/challenges/${id}`))
      .data;

    Store.dispatch(setChallengeData(challenge));
    return challenge;
  }

  public async submitChallenge(id: string, token?: string): Promise<Challenge> {
    const config = await this.getReCaptchaConfig();

    return (
      await this.axios.post<Challenge>(
        `/challenges/${id}/achieve`,
        { token },
        config
      )
    ).data;
  }

  /**
   * Ticket Code Endpoints:
   */
  public async checkTicket(code: string): Promise<TicketCode> {
    return (
      await this.axios.post<TicketCode>('/lottery-ticket-codes/check', {
        campaignId: getCampaignId(Store.getState()),
        code
      })
    ).data;
  }

  public async claimTicket(id: string): Promise<Prize> {
    const {
      lotteryPrize: { prize }
    } = (
      await this.axios.post<TicketCodeResponse>(
        `/lottery-ticket-codes/${id}/lottery`
      )
    ).data;

    Store.dispatch(setUserPrize(prize));
    return prize;
  }
}

export default new APIService();
